// drawQueue.ts — Patch 010.C.2.2 (Deep Draws, sub-patch 2 of 3).
//
// The draw queue: AI-intaken invoices in a review state, the human-approve
// gate, and per-draw lien-waiver tracking. Backend: draw_invoices review
// columns + draw_invoice_receipts + draw_lien_waivers (migration 0066) +
// workers/core-os/src/routes/draw_queue.ts.

import { request } from './request'

export type DrawReviewState = 'pending_review' | 'approved' | 'rejected'

export interface DrawQueueInvoice {
  id: string
  draw_request_id: string
  draw_number: number
  vendor_company_name: string | null
  vendor_profile_id: string | null
  invoice_number: string | null
  invoice_date: string | null
  amount_cents: number
  scs_line_id: string | null
  status: string
  review_state: DrawReviewState
  mapping_confidence: number | null
  mismatch_flags_json: string | null
  source_inbound_email_id: string | null
  notes: string | null
  // 2026-08-24 — reimbursement flag (migration 0127). vendor_company_name
  // stays the real vendor on record; when true, the draw check payee
  // becomes "Heritage Homes LLC" instead.
  paid_by_heritage: number
  // 2026-08-24 — generalized reimbursement payee (migration 0128). When
  // set, overrides the check payee to this name (e.g. the borrower paid
  // the vendor directly) instead of "Heritage Homes LLC" / the vendor.
  reimburse_payee_name: string | null
  // FK to documents — the source invoice PDF. When an AI extraction splits
  // one invoice across multiple SCS lines, every split row shares this
  // value (draw_intake.ts / draws.ts re-extract), so it's the reliable key
  // for regrouping splits back into "one invoice" in the UI.
  invoice_document_id: string | null
  receipt_count: number
  created_at: string
  updated_at: string | null
}

export interface DrawQueueResponse {
  ok: true
  invoices: DrawQueueInvoice[]
  summary: { pending_review: number; approved: number; rejected: number }
  count: number
}

export interface DrawInvoiceReceipt {
  id: string
  document_id: string
  document_filename: string
  source_inbound_email_id: string | null
  amount_cents: number | null
  notes: string | null
  matched_at: string | null
  created_at: string
}

export interface DrawInvoiceDetailResponse {
  ok: true
  invoice: Record<string, unknown>
  receipts: DrawInvoiceReceipt[]
}

export interface DrawLienWaiver {
  id: string
  draw_request_id: string
  sub_profile_id: string | null
  sub_company_name: string | null
  waiver_type: 'conditional' | 'unconditional'
  document_id: string | null
  source_inbound_email_id: string | null
  received_at: string | null
  notes: string | null
  created_at: string
  updated_at: string | null
}

export interface DrawIntakeResult {
  ok: true
  scanned: number
  drafted: number
  skipped_already_filed: number
  failed: number
  errors: string[]
  draft_draw_request_id: string | null
}

export interface PatchDrawInvoiceInput {
  vendor_company_name?: string
  amount_cents?: number
  invoice_number?: string
  invoice_date?: string | null
  scs_line_id?: string | null
  notes?: string
  review_state?: DrawReviewState
  // Patch 016.1.U — bulk-archive flag. Backend route (draw_queue.ts
  // patchInvoice) already supports archived: true (sets archived_at);
  // frontend type just needed widening.
  archived?: boolean
  paid_by_heritage?: boolean
  reimburse_payee_name?: string | null
}

export interface ScsLineLite {
  id: string
  line_number: number | null
  display_name: string
  category: string | null
  current_budget_cents: number
}

export interface CreateLienWaiverInput {
  sub_profile_id?: string
  sub_company_name?: string
  waiver_type: 'conditional' | 'unconditional'
  document_id?: string
  received_at?: string
  notes?: string
}

const pid = (id: string): string => encodeURIComponent(id)

export const drawQueueApi = {
  intake: (projectId: string) =>
    request<DrawIntakeResult>(`/api/projects/${pid(projectId)}/draw-queue/intake`, {
      method: 'POST',
      body: '{}',
    }),
  queue: (
    projectId: string,
    reviewState?: DrawReviewState,
    options: { includeCompiled?: boolean } = {},
  ) => {
    const params = new URLSearchParams()
    if (reviewState) params.set('review_state', reviewState)
    // Patch 016.1.U — default hides invoices on draws that have
    // already been compiled (sent to title company). Toggle to see them.
    if (options.includeCompiled) params.set('include_compiled', 'true')
    const qs = params.toString()
    return request<DrawQueueResponse>(
      `/api/projects/${pid(projectId)}/draw-queue${qs ? `?${qs}` : ''}`,
    )
  },
  invoice: (projectId: string, invoiceId: string) =>
    request<DrawInvoiceDetailResponse>(
      `/api/projects/${pid(projectId)}/draw-invoices/${pid(invoiceId)}`,
    ),
  updateInvoice: (
    projectId: string,
    invoiceId: string,
    body: PatchDrawInvoiceInput,
  ) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/projects/${pid(projectId)}/draw-invoices/${pid(invoiceId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  attachReceipt: (
    projectId: string,
    invoiceId: string,
    body: { document_id: string; amount_cents?: number; notes?: string },
  ) =>
    request<{ ok: true; id: string; request_id: string }>(
      `/api/projects/${pid(projectId)}/draw-invoices/${pid(invoiceId)}/receipts`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  removeReceipt: (projectId: string, invoiceId: string, receiptId: string) =>
    request<{ ok: true; id: string }>(
      `/api/projects/${pid(projectId)}/draw-invoices/${pid(invoiceId)}/receipts/${pid(receiptId)}`,
      { method: 'DELETE' },
    ),
  lienWaivers: (projectId: string, drawId: string) =>
    request<{ ok: true; lien_waivers: DrawLienWaiver[] }>(
      `/api/projects/${pid(projectId)}/draws/${pid(drawId)}/lien-waivers`,
    ),
  createLienWaiver: (
    projectId: string,
    drawId: string,
    body: CreateLienWaiverInput,
  ) =>
    request<{ ok: true; id: string; request_id: string }>(
      `/api/projects/${pid(projectId)}/draws/${pid(drawId)}/lien-waivers`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateLienWaiver: (
    projectId: string,
    waiverId: string,
    body: { received_at?: string | null; notes?: string; archived?: boolean },
  ) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/projects/${pid(projectId)}/lien-waivers/${pid(waiverId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  // SCS budget lines (015.6 endpoint) — the re-mapping picker in the queue.
  scsLines: (projectId: string) =>
    request<{ ok: true; scs_lines: ScsLineLite[] }>(
      `/api/projects/${pid(projectId)}/scs-lines`,
    ),
  // Patch 016.1.O — re-extract a single-row invoice into multi-SCS-line
  // splits. Archives the original row, creates N new pending_review rows
  // summing to the original amount. See workers/core-os/src/routes/draws.ts
  // for the backend handler (Patch 016.1.N).
  reExtract: (projectId: string, invoiceId: string) =>
    request<{
      ok: true
      original_invoice_id: string
      new_invoice_ids: string[]
      splits: Array<{
        description: string
        amount_cents: number
        scs_line_id: string | null
        confidence: number
        allocation_method:
          | 'explicit'
          | 'scaled_explicit'
          | 'scs_proportional'
          | 'equal_fallback'
      }>
      payment_meta: {
        payment_kind:
          | 'payment_of_n'
          | 'deposit'
          | 'retainage'
          | 'final_payment'
          | 'progress_billing'
          | null
        payment_n_of_m: { current: number; total: number } | null
      }
      request_id: string
    }>(
      `/api/projects/${pid(projectId)}/draw-invoices/${pid(invoiceId)}/re-extract`,
      { method: 'POST', body: '{}' },
    ),
}
